<script setup>
import { SENTIMENT_SEGMENTS } from "@/lib/chartPalettes";
import { Search } from "lucide-vue-next";
import { formatPercent } from "@/composables/useChartUtils";
import { renderMarkdownInline } from "@utils/renderMarkdown";

const props = defineProps({
  item: { type: Object, required: true },
  itemIdx: { type: Number, required: true },
  populations: { type: Array, required: true },
  showSignificance: { type: Boolean, default: false },
  showSentiment: { type: Boolean, default: false },
  clickable: { type: Boolean, default: false },
});

const emit = defineEmits(["bar-click"]);

function resolveItem(pop) {
  return pop.data[props.itemIdx] || props.item;
}

function sigIcon(item) {
  if (item.significance === "+") return "up";
  if (item.significance === "-") return "down";
  return null;
}

function sigDetailsText(item) {
  const d = item.significanceDetails;
  if (!d) return null;
  const parts = [];
  if (d.superiorTo?.length) parts.push(`Superior to: ${d.superiorTo.join(", ")}`);
  if (d.inferiorTo?.length) parts.push(`Inferior to: ${d.inferiorTo.join(", ")}`);
  return parts.length ? parts.join(" · ") : null;
}
</script>

<template>
  <div>
    <div class="font-medium mb-1" v-html="renderMarkdownInline(item.label)" />
    <div class="grid gap-1">
      <div
        v-for="pop in populations"
        :key="pop.label">
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-1.5">
            <div class="h-2.5 w-2.5 shrink-0 rounded-xs" :style="{ backgroundColor: resolveItem(pop).color || pop.color }" />
            <span class="text-primary-foreground/70">{{ pop.label }}</span>
          </div>
          <span class="font-medium tabular-nums flex items-center gap-1">
            {{ formatPercent(resolveItem(pop).value) }}
            <span class="text-primary-foreground/70 font-normal">(n={{ resolveItem(pop).count }})</span>
            <span
              v-if="showSignificance && sigIcon(resolveItem(pop)) === 'up'"
              class="text-3xs text-status-success"
            >▲</span>
            <span
              v-if="showSignificance && sigIcon(resolveItem(pop)) === 'down'"
              class="text-3xs text-status-error"
            >▼</span>
          </span>
        </div>
        <div
          v-if="showSentiment && resolveItem(pop).sentimentBreakdown"
          class="flex items-center justify-center gap-3 mt-0.5">
          <span
            v-for="segment in SENTIMENT_SEGMENTS"
            :key="segment.key"
            class="flex items-center gap-1">
            <span class="inline-block h-2 w-2 shrink-0 rounded-full" :style="{ backgroundColor: segment.color }" />
            <span class="text-primary-foreground/70">{{ segment.label }}</span>
            <span class="font-medium tabular-nums">{{ formatPercent(resolveItem(pop).sentimentBreakdown[segment.key]) }}</span>
          </span>
        </div>
      </div>
      <div
        v-for="pop in populations"
        :key="`sig-${pop.label}`"
      >
        <div
          v-if="showSignificance && sigDetailsText(resolveItem(pop))"
          class="text-3xs text-primary-foreground/70 mt-0.5"
        >
          <span class="inline-block h-2.5 w-2.5 shrink-0 rounded-xs mr-1" :style="{ backgroundColor: resolveItem(pop).color || pop.color }" />
          {{ sigDetailsText(resolveItem(pop)) }}
        </div>
      </div>
    </div>
    <div v-if="item.subtotalOf?.length" class="mt-1.5 pt-1.5 border-t border-border/50 text-3xs text-muted-foreground">
      <div v-for="label in item.subtotalOf" :key="label" v-html="renderMarkdownInline(label)" />
    </div>
    <button
      v-if="clickable"
      class="flex items-center gap-1 mt-1.5 pt-1.5 border-t border-primary-foreground/20 text-xs text-primary-foreground/70 hover:text-primary-foreground transition-colors w-full"
      aria-label="See verbatims for this answer"
      @click="emit('bar-click', item)"
    >
      <Search class="h-3 w-3" />
      See verbatims
    </button>
  </div>
</template>
